<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <h4>Device's SIM Details (#{{ device.slno }})</h4>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
            </v-card-title>

            <v-container>
                <v-alert v-if="errors.length" class="mb-3 bg-error" density="compact" variant="tonal">
                    <p v-for="error in errors" :key="error">{{ error }}</p>
                </v-alert>
                <v-row>
                    <v-col cols="12">
                        <v-table>
                            <tbody>
                            <template v-if="!simDetails">
                                <tr>
                                    <td class="text-center" colspan="4">
                                        <v-progress-circular indeterminate size="small"></v-progress-circular>
                                    </td>
                                </tr>
                            </template>
                            <template v-else>
                                <tr>
                                    <td class="font-weight-bold">ICCID</td>
                                    <td>{{ simDetails?.iccid ?? '' }}</td>
                                    <td class="font-weight-bold">IMSI</td>
                                    <td>{{ simDetails?.imsi ?? '' }}</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">MSISDN</td>
                                    <td>{{ simDetails?.msisdn ?? '' }}</td>
                                    <td class="font-weight-bold">IMEI</td>
                                    <td>{{ simDetails?.imei ?? '' }}</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">IMEI_LOCK</td>
                                    <td>{{ simDetails?.imei_lock ?? '' }}</td>
                                    <td class="font-weight-bold">Status</td>
                                    <td>{{ simDetails?.status ?? '' }}</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">IP Address</td>
                                    <td>{{ simDetails?.ip_address ?? '' }}</td>
                                    <td class="font-weight-bold">Activation Date</td>
                                    <td>{{ simDetails?.activation_date ?? '' }}</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Current Quota</td>
                                    <td>{{ simDetails?.current_quota ?? '' }}</td>
                                    <td class="font-weight-bold">Current Quota SMS</td>
                                    <td>{{ simDetails?.current_quota_SMS ?? '' }}</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Current Quota</td>
                                    <td>{{ simDetails?.current_quota ?? '' }}</td>
                                    <td class="font-weight-bold">Quota Status</td>
                                    <td>{{ simDetails?.quota_status?.description ?? '' }}</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Current Quota SMS</td>
                                    <td>{{ simDetails?.current_quota_SMS ?? '' }}</td>
                                    <td class="font-weight-bold">Quota Status SMS</td>
                                    <td>{{ simDetails?.quota_status_SMS?.description ?? '' }}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <v-btn :color="simDetails?.status === 'Enabled' ? 'error' : 'success'" size="small"
                                               @click="toggleSimStatus">
                                            {{ simDetails?.status === 'Enabled' ? 'Disable Sim' : 'Enable Sim' }}
                                        </v-btn>
                                    </td>
                                </tr>
                                <tr v-if="Object.keys(debugEntries).length">
                                    <td colspan="4" class="pt-2">
                                        <v-expansion-panels variant="accordion">
                                            <v-expansion-panel title="All fields (debug)">
                                                <v-expansion-panel-text>
                                                    <v-table density="compact">
                                                        <tbody>
                                                        <tr v-for="(val, key) in debugEntries" :key="key">
                                                            <td class="font-weight-bold">{{ key }}</td>
                                                            <td style="word-break:break-all;">{{ val }}</td>
                                                        </tr>
                                                        </tbody>
                                                    </v-table>
                                                </v-expansion-panel-text>
                                            </v-expansion-panel>
                                        </v-expansion-panels>
                                    </td>
                                </tr>

                            </template>
                            </tbody>
                        </v-table>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn size="small" type="button" @click="$emit('update:modelValue',false)">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ref, onMounted, computed} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    device: Object
});
const emits = defineEmits(['update:modelValue']);
const dialogSwitch = ref(props.modelValue);
const simDetails = ref(null);
const errors = ref([]);

// Flatten every field the API returned (any vendor) into key/value rows for the
// debug panel; nested objects/arrays are shown as JSON, http_status is hidden.
const debugEntries = computed(() => {
    const d = simDetails.value;
    if (!d || typeof d !== 'object') {
        return {};
    }
    const out = {};
    Object.keys(d).forEach((k) => {
        if (k === 'http_status') {
            return;
        }
        const v = d[k];
        out[k] = (v !== null && typeof v === 'object') ? JSON.stringify(v) : v;
    });
    return out;
});

onMounted(() => {
    getSimStatus()
})

const getSimStatus = () => {
    errors.value = [];
    simDetails.value = null
    send('get', `devices/${props.device.id}/sim-details`)
        .then((response) => {
            if (response.data.success) {
                simDetails.value = response.data.data;
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

const toggleSimStatus = () => {
    errors.value = [];
    if (!simDetails.value) {
        errors.value.push("No sim details found.");
        return false
    }
    const confirmation = confirm("Are you sure to " + (simDetails.value.status === 'Enabled' ? 'Disable' : 'Enable') + ` SIM of # ${props.device.slno}`)
    if (!confirmation) {
        return false
    }
    send('post', `devices/${props.device.id}/toggle-sim-status`, {
        'status': simDetails.value.status === 'Enabled' ? 0 : 1
    })
        .then((response) => {
            if (response.data.success) {
                simDetails.value = null;
                snackbar.showSnackbar(response.data.message);
                setTimeout(getSimStatus, 3000);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>
